import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useEffect, useState, useMemo } from 'react';
import { useColorModeValue, useDisclosure } from '@chakra-ui/react';
import {
  useStartWorkflow,
  useWorkflowTasks,
  useSubmitText,
  useSubmitMedia,
  useSubmitChoice,
  useConfirmWip,
} from '@/lib/sdk/workflows/queries';
import { useAuth } from '@/lib/sdk/auth/hooks';
import { useToast } from '@/client/hooks/useToast';
import { TaskType, TaskAction } from '@/lib/sdk/chatbot/types';
import type { MediaPayload, ChannelResponse, WebTaskItem, TaskContext } from '@/lib/sdk/workflows/types';
import type { KanbanTask } from '../components/KanbanBoard';
import type { TaskCardStatus } from '../components/TaskCardV2';

const hiddenTaskTypes = new Set([TaskType.INTRO, TaskType.END, TaskType.TASKLIST]);

export function useWorkflowPage() {
  const { botCode } = useParams<{ botCode: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { currentOrg, user } = useAuth();
  const toast = useToast();

  const existingSourceKey = searchParams.get('source_key');
  const [sourceKey, setSourceKey] = useState(existingSourceKey || '');
  const [isStarting, setIsStarting] = useState(!existingSourceKey);
  const [selectedTaskRepId, setSelectedTaskRepId] = useState<string | null>(null);

  const { isOpen, onOpen, onClose } = useDisclosure();

  const cardGradient = useColorModeValue(
    'linear(to-br, white, brand.50)',
    'linear(to-br, gray.800, gray.700)'
  );

  const userId = (user?.country_code || '') + (user?.phone_number || '');
  const orgId = currentOrg?.id || '';

  const startWorkflow = useStartWorkflow({
    onSuccess: (response: ChannelResponse) => {
      setSourceKey(response?.task_rep?.source_key || '');
      setIsStarting(false);
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to start workflow');
      setIsStarting(false);
      navigate(`/dashboard/${orgId}/workflows`);
    },
  });

  useEffect(() => {
    if (botCode && userId && orgId && !sourceKey && !existingSourceKey) {
      startWorkflow.mutate({ userId, orgId, botCode });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [botCode, userId, orgId, existingSourceKey]);

  const {
    data: workflowData,
    isLoading: isLoadingTasks,
    isRefetching,
    error: tasksError,
    refetch: refetchTasks,
  } = useWorkflowTasks(botCode || '', sourceKey);

  const selectedTask = useMemo(() => {
    if (!selectedTaskRepId || !workflowData?.tasks) return null;
    return workflowData.tasks.find(t => t.task?.id?.toString() === selectedTaskRepId) ?? null;
  }, [selectedTaskRepId, workflowData?.tasks]);

  const isSelectedTaskWipConfigured = useMemo(() => {
    if (!selectedTask || !workflowData?.chatbot?.task_list) return false;
    const taskProps = workflowData.chatbot.task_list.find(tp => tp.id === selectedTask.task.task_id);
    return taskProps?.task_actions?.includes(TaskAction.WIP) ?? false;
  }, [selectedTask, workflowData?.chatbot?.task_list]);

  const submitText  = useSubmitText(botCode || '');
  const submitMedia = useSubmitMedia(botCode || '');
  const submitChoice = useSubmitChoice(botCode || '');
  const confirmWip  = useConfirmWip(botCode || '');

  const getTaskContext = (): TaskContext | null => {
    if (!selectedTask || !botCode) return null;
    return { botCode, taskId: selectedTask.task.task_id, repId: Number(selectedTask.task.id) };
  };

  const handleOpenTask = (task: WebTaskItem) => {
    if (task.is_blocked) {
      toast.error('This task is blocked until the previous approval task is completed');
      return;
    }
    setSelectedTaskRepId(task.task.id?.toString() ?? null);
    onOpen();
  };

  const handleCloseModal = () => {
    setSelectedTaskRepId(null);
    onClose();
    refetchTasks();
  };

  const handleSubmitText = async (value: string) => {
    const ctx = getTaskContext();
    if (!ctx) { toast.error('No task selected'); return; }
    try {
      await submitText.mutateAsync({ userId, orgId, text: value, taskContext: ctx });
      toast.success('Response submitted successfully');
      refetchTasks();
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : 'Failed to submit response');
      throw error;
    }
  };

  const handleSubmitMedia = async (media: MediaPayload) => {
    const ctx = getTaskContext();
    if (!ctx) { toast.error('No task selected'); return; }
    try {
      await submitMedia.mutateAsync({ userId, orgId, media, taskContext: ctx });
      toast.success('Media submitted successfully');
      refetchTasks();
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : 'Failed to submit media');
      throw error;
    }
  };

  const handleSubmitChoice = async (choiceId: string, choiceText: string) => {
    const ctx = getTaskContext();
    if (!ctx) { toast.error('No task selected'); return; }
    try {
      await submitChoice.mutateAsync({ userId, orgId, choiceId, choiceText, taskContext: ctx });
      toast.success('Choice submitted successfully');
      refetchTasks();
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : 'Failed to submit choice');
      throw error;
    }
  };

  const handleConfirmWip = async (confirm: 'yes' | 'no') => {
    const ctx = getTaskContext();
    if (!ctx) { toast.error('No task selected'); return; }
    try {
      await confirmWip.mutateAsync({ userId, orgId, confirm, taskContext: ctx });
      if (confirm === 'yes') toast.success('Task marked as complete');
      refetchTasks();
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : 'Failed to confirm task');
      throw error;
    }
  };

  const isEventView = !!existingSourceKey;
  const backPath  = isEventView ? `/dashboard/${orgId}/workflows/instant` : `/dashboard/${orgId}/workflows/active`;
  const backLabel = isEventView ? 'Back to Events' : 'Back to Workflows';
  const breadcrumbs = [
    { label: 'Dashboard', href: `/dashboard/${orgId}/reports` },
    { label: isEventView ? 'Events' : 'Workflows', href: backPath },
    { label: workflowData?.chatbot.title || 'Workflow', href: '' },
  ];

  const allTasks = workflowData?.tasks ?? [];
  const tasks = allTasks.filter(t => !hiddenTaskTypes.has(t.task.rep_type as TaskType));

  const kanbanTasks = useMemo((): KanbanTask[] => {
    const now = new Date();
    return tasks.map((taskItem, index) => {
      const isCompleted   = taskItem.task.rep_status === 1;
      const isBlocked     = taskItem.is_blocked;
      const isWip         = taskItem.task.notes_enabled_mode === 'wip';
      const softDue       = taskItem.task.soft_due_date ? new Date(taskItem.task.soft_due_date) : null;
      const isSoftOverdue = !isCompleted && softDue !== null && softDue < now;
      const isDueSoon     = !isCompleted && softDue !== null && !isSoftOverdue &&
        (softDue.getTime() - now.getTime()) <= 24 * 60 * 60 * 1000;
      const status: TaskCardStatus = isBlocked       ? 'blocked'
        : isCompleted   ? 'completed'
        : isWip         ? 'wip'
        : isSoftOverdue ? 'soft_overdue'
        : isDueSoon     ? 'due_soon'
        : 'pending';
      return { taskItem, status, index };
    });
  }, [tasks]);

  const completedTasks = tasks.filter(t => t.task.rep_status === 1).length;
  const totalTasks = tasks.length;
  const progress = totalTasks > 0 ? (completedTasks / totalTasks) * 100 : 0;
  const isSubmitting = submitText.isPending || submitMedia.isPending || submitChoice.isPending || confirmWip.isPending;

  return {
    navigate, orgId,
    isStarting, isLoadingTasks, tasksError,
    workflowData, tasks, kanbanTasks,
    completedTasks, totalTasks, progress,
    isRefetching, refetchTasks,
    isEventView, backPath, backLabel, breadcrumbs,
    cardGradient,
    isOpen, handleCloseModal,
    selectedTask,
    isSelectedTaskWipConfigured,
    handleOpenTask,
    handleSubmitText, handleSubmitMedia, handleSubmitChoice, handleConfirmWip,
    isSubmitting,
    isConfirmingWip: confirmWip.isPending,
  };
}
